import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft, Clock, BarChart3, Check } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [user, slug]);

  if (loading || (!course && !notFound)) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0B0C10]">
        <div className="h-10 w-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="grain bg-[#0B0C10] min-h-screen"><Navbar />
        <div className="pt-40 px-6 text-center"><h1 className="font-display text-3xl text-white">Course not found</h1>
          <Link to="/dashboard" className="text-[#D4AF37] mt-4 inline-block">Back to library</Link></div>
      </div>
    );
  }

  const lesson = course.lessons[active];

  return (
    <div className="grain bg-[#0B0C10] min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-12 px-6 md:px-12 lg:px-24 border-b border-[#1F222E]">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white transition-colors" data-testid="course-back">
          <ArrowLeft size={16} /> Back to library
        </Link>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mt-8">{course.category}</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white font-light mt-4 max-w-3xl">{course.title}</h1>
        <p className="text-[#9CA3AF] mt-5 max-w-2xl">{course.summary}</p>
        <div className="flex items-center gap-6 mt-6 text-sm text-[#6B7280]">
          <span className="flex items-center gap-2"><Clock size={16} /> {course.duration}</span>
          <span className="flex items-center gap-2"><BarChart3 size={16} /> {course.level}</span>
        </div>
      </section>

      <section className="relative z-10 py-16 px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-5">Lessons</p>
          <div className="space-y-px bg-[#1F222E] border border-[#1F222E]">
            {course.lessons.map((l, i) => (
              <button key={i} onClick={() => setActive(i)}
                className={`w-full text-left p-5 flex items-start gap-4 transition-colors ${i === active ? "bg-[#16181f]" : "bg-[#12141A] hover:bg-[#16181f]"}`}
                data-testid={`lesson-tab-${i}`}>
                <span className={`h-7 w-7 shrink-0 grid place-items-center text-xs border rounded-full ${i === active ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#3a3e4c] text-[#6B7280]"}`}>{i + 1}</span>
                <span className={`text-sm ${i === active ? "text-white" : "text-[#9CA3AF]"}`}>{l.title}</span>
              </button>
            ))}
          </div>
        </aside>

        <article className="lg:col-span-8 border border-[#1F222E] bg-[#12141A] p-8 md:p-12" data-testid="lesson-content">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Lesson {active + 1} of {course.lessons.length}</p>
          <h2 className="font-display text-3xl text-white font-light mt-4">{lesson.title}</h2>
          <p className="text-[#9CA3AF] leading-relaxed text-lg mt-6">{lesson.body}</p>

          <div className="flex justify-between mt-12 pt-8 border-t border-[#1F222E]">
            <button disabled={active === 0} onClick={() => setActive((a) => a - 1)}
              className="text-sm text-[#9CA3AF] hover:text-white disabled:opacity-30 transition-colors" data-testid="lesson-prev">← Previous</button>
            {active < course.lessons.length - 1 ? (
              <button onClick={() => setActive((a) => a + 1)} className="text-sm text-[#D4AF37] hover:text-[#F1D570]" data-testid="lesson-next">Next lesson →</button>
            ) : (
              <span className="text-sm text-[#10B981] flex items-center gap-2"><Check size={16} /> Course complete</span>
            )}
          </div>
        </article>
      </section>
      <Footer />
    </div>
  );
}
